import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import {
    Component,
    ElementRef,
    input,
    OnInit,
    viewChild,
} from '@angular/core';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { FilterObject } from '@app/shared/models/components/filter-object';
import {
    hasStatus,
    hasDeliveryNoteBundleStatus,
    hasDateClosed,
    TableRowData,
} from '@app/shared/models/table-data-types';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatButton } from '@angular/material/button';

export interface FilterKeys {
    keyName: string;
    label: string;
}

const STORAGE_KEY = 'table-filter-state';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    providers: [DatePipe],
    imports: [
        MatFormField,
        MatLabel,
        MatIcon,
        MatPrefix,
        MatInput,
        ReactiveFormsModule,
        FormsModule,
        MatSuffix,
        MatMenuTrigger,
        MatCheckbox,
        MatMenu,
        MatRadioGroup,
        MatRadioButton,
        MatButton,
        TranslateModule,
    ],
})
export class TableFilterComponent implements OnInit {
    filterSearch = viewChild<ElementRef<HTMLInputElement>>('filterSearch');
    menuTrigger = viewChild(MatMenuTrigger);

    isTours = input<boolean>(false);
    dataSource = input<MatTableDataSource<any>>();
    hasCheckBoxFilter = input<boolean>(true);
    keysForFilteringObj = input<FilterKeys[]>();
    ofContext = input<boolean>(false);
    showClosedLabel = input<string>();

    _keysForFilteringObj: FilterKeys[] | undefined;
    textFilter: string = '';
    checkboxFilter: boolean = true;
    searchDisabled: boolean = false;
    filterBy: string;
    statuses: FilterKeys[] = [
        {
            keyName: 'open',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
        },
        {
            keyName: 'sent',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
        },
        {
            keyName: 'closed',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
        },
    ];
    boolStatus: FilterKeys[] = [
        {
            keyName: 'true',
            label: 'TRUE',
        },
        {
            keyName: 'false',
            label: 'FALSE',
        },
    ];
    isStatus: boolean = false;
    selection = new SelectionModel<any>(true, []);

    constructor(
        private datePipe: DatePipe,
        private translate: TranslateService,
        private sessionService: SessionService
    ) {}

    ngOnInit(): void {
        this.checkboxFilter = true;
        this._keysForFilteringObj = this.keysForFilteringObj();
        this.loadFilterState();
        this.setFilterPredicate();
        this.applyFilter();
    }

    private loadFilterState(): void {
        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
            const state = JSON.parse(savedState);
            this.checkboxFilter = state.showClosed ?? true;
        }
    }

    // Helper function to get nested property value using path string like 'properties.referenceNumber'
    private getNestedPropertyValue(obj: any, path: string): any {
        if (!obj || !path) return undefined;

        const properties = path.split('.');
        let value = obj;

        for (const prop of properties) {
            if (value === null || value === undefined) {
                return undefined;
            }
            value = value[prop];
        }

        return value;
    }

    private setFilterPredicate(): void {
        this.dataSource()!.filterPredicate = (
            data: TableRowData,
            filter: string
        ): boolean => {
            let matching: boolean = false;
            const filterObj: FilterObject = JSON.parse(filter);

            // Check if item should be shown based on closed status
            // Check both status field and dateClosed field for compatibility
            const isClosed =
                (hasStatus(data) && data.status === 'closed') ||
                (hasDeliveryNoteBundleStatus(data) &&
                    data.deliveryNoteBundleStatus === 'closed') ||
                (hasDateClosed(data) && data.dateClosed > 0);

            if (
                filterObj.checkClosed ||
                (!filterObj.checkClosed && !isClosed)
            ) {
                if (!filterObj.text || filterObj.text.length === 0) {
                    matching = false;
                }

                this._keysForFilteringObj?.forEach((filterkey: FilterKeys) => {
                    // Get the value using the nested property helper
                    let dataOfRow: any = this.getNestedPropertyValue(
                        data,
                        filterkey.keyName
                    );

                    if (dataOfRow !== null && dataOfRow !== undefined) {
                        if (typeof dataOfRow === 'number' && dataOfRow > 0) {
                            dataOfRow = this.datePipe.transform(dataOfRow);
                        }

                        if (
                            filterkey.keyName === 'status' ||
                            filterkey.keyName === 'deliveryNoteBundleStatus'
                        ) {
                            if (dataOfRow.toString() === 'open')
                                matching =
                                    matching ||
                                    'offen'.includes(filterObj.text);
                            if (dataOfRow.toString() === 'sent')
                                matching =
                                    matching ||
                                    'versendet'.includes(filterObj.text);
                            if (dataOfRow.toString() === 'closed')
                                matching =
                                    matching ||
                                    'geschlossen'.includes(filterObj.text);
                        }

                        if (filterkey.keyName === 'palletTickets') {
                            if (dataOfRow.toString() === 'true')
                                matching =
                                    matching || 'ja'.includes(filterObj.text);
                            if (dataOfRow.toString() === 'false')
                                matching =
                                    matching || 'nein'.includes(filterObj.text);
                        }

                        if (filterkey.keyName === 'discrepancies') {
                            if (dataOfRow.toString() === 'true')
                                matching =
                                    matching || 'ja'.includes(filterObj.text);
                            if (dataOfRow.toString() === 'false')
                                matching =
                                    matching || 'nein'.includes(filterObj.text);
                        }

                        matching =
                            matching ||
                            dataOfRow
                                .toString()
                                .toLowerCase()
                                .includes(filterObj.text);
                    }
                });
            }
            return matching;
        };
    }

    onFilterChanges(filterKey: FilterKeys) {
        this.textFilter = '';
        this.closeFilterMenu();
        this._keysForFilteringObj = [filterKey];
        this.isStatus = false;
        this.applyFilter();
    }

    onStatusFilterChanges(filterKey: FilterKeys) {
        if (
            this.keysForFilteringObj()?.some(
                key => key.keyName === 'deliveryNoteBundleStatus'
            )
        ) {
            this._keysForFilteringObj = [
                { keyName: 'deliveryNoteBundleStatus', label: 'STATUS.STATE' },
            ];
        } else {
            this._keysForFilteringObj = [
                { keyName: 'status', label: 'STATUS.STATE' },
            ];
        }

        this.textFilter = this.translate.instant(filterKey.label);
        this.applyFilter();
        this.isStatus = true;
    }

    onPalletFilterChanges(filterKey: FilterKeys) {
        this._keysForFilteringObj = [
            {
                keyName: 'palletTickets',
                label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
            },
        ];

        this.textFilter = this.translate.instant(filterKey.label);
        this.applyFilter();
    }

    onDiscrepanciesFilterChanges(filterKey: FilterKeys) {
        this._keysForFilteringObj = [
            {
                keyName: 'discrepancies',
                label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
            },
        ];
        this.textFilter = this.translate.instant(filterKey.label);
        this.applyFilter();
    }

    private saveFilterState(): void {
        const state = {
            showClosed: this.checkboxFilter,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }

    async applyFilter(ev?: any) {
        const userSession = this.sessionService.getSession();
        if (ev && ev === userSession.emailAddress) {
            this.filterSearch()!.nativeElement.value = '';
            this.resetFilter();
            return;
        }

        const filter: FilterObject = {
            text: this.textFilter?.toLowerCase(),
            checkClosed: this.checkboxFilter,
        };
        this.dataSource()!.filter = JSON.stringify(filter);
        this.saveFilterState();
    }

    resetFilter() {
        this.filterBy = '';
        this.textFilter = '';
        this._keysForFilteringObj = this.keysForFilteringObj();
        this.applyFilter();
        this.closeFilterMenu();
    }

    closeFilterMenu() {
        this.menuTrigger()!.closeMenu();
    }
}
